import { Component, Inject, Input, OnChanges, OnDestroy, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { YTBesprechung } from '../../../../generated-api';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import * as Constants from '../../../../core/helpers/constants/constants'
import {
  LegendDialogComponent, LegendDialogModes
} from '../../../../shared/components/legend-dialog/legend-dialog.component';
import { isPlatformBrowser } from '@angular/common';

import titleReplacements from '../../../../../assets/config/title-replacements.json';

@Component({
  selector: 'app-ytbesprechung-detail',
  templateUrl: './ytbesprechung-detail.component.html',
  styleUrls: ['./ytbesprechung-detail.component.scss'],
  standalone: false
})
export class YtbesprechungDetailComponent implements OnChanges, OnDestroy {
  @Input() ytbesprechung: YTBesprechung;
  @Input() loading = true;
  subs = new SubSink();
  picture;

  get keywords(): any[] {
    return this.ytbesprechung ? Object.keys(this.ytbesprechung.keywords).map(value => this.ytbesprechung.keywords[value]) : [];
  }

  constructor(private matDialog: MatDialog, @Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ytbesprechung && changes.ytbesprechung.currentValue) {
      this.picture = this.ytbesprechung.image;

      // json-ld ala https://javascript.plainenglish.io/how-to-use-json-ld-for-advanced-seo-in-angular-63528c98bb91
      if (isPlatformBrowser(this.platformId)) {
        let script;
        let shouldAppend = false;
        if (document.head.getElementsByClassName('structured-data').length) {
          script = document.head.getElementsByClassName('structured-data')[0];
        } else {
          script = document.createElement('script');
          shouldAppend = true;
        }
        // TODO: auch ytbesprechung! plus #keywords, plus positiv/negativ, plus alter
        script.setAttribute('class', 'structured-data');
        script.type = 'application/ld+json';

        if (this.ytbesprechung != null) {
          let reviewBody = this.ytbesprechung.content;
          if (this.ytbesprechung.warnung) reviewBody += '\n\nAchtung: ' + this.ytbesprechung.warnung;

          const schemaOrg = {
            '@context': 'http://schema.org',
            '@type': 'CriticReview',
            'publisher': {
              '@type': 'Organization',
              'sameAs': '/redtext/101225/Herausgeber',
              'name': 'Programmberatung für Eltern e.V.'
            },
            'author': {
              '@type': 'Organization',
              'sameAs': '/redtext/101237/Redaktion',
              'name': 'JFF – Institut für Medienpädagogik in Forschung und Praxis'
            },
            'inLanguage': 'German',
            'url': '/ytbesprechung/' + this.ytbesprechung.id + '/' + this.prettifyUrl(this.ytbesprechung.title),
            'datePublished': this.ytbesprechung.moddate ?? '2025-04-23',
            'headline': this.ytbesprechung.title,
            'description': this.ytbesprechung.content?.substr(0, 198) + '..',
            'reviewBody': reviewBody,
            'itemReviewed': {
              '@type': 'Organization',
              'name': this.ytbesprechung.title,
              'image': this.ytbesprechung.image,
              // sameAs links go here below
            },
            'reviewRating': {
              '@type': 'Rating',
              'ratingValue': (this.ytbesprechung.rating.id == 4) ? 'FLIMMO findet\'s gut! (ab ' + this.alterWort(this.ytbesprechung.alter) + ')' :
                (this.ytbesprechung.rating.id == 3) ? 'Grün (ab ' + this.alterWort(this.ytbesprechung.alter) + ')' :
                  (this.ytbesprechung.rating.id == 2) ? 'Gelb (aus pädagogischer Sicht problematisch)' :
                    'Rot (nicht für Kinder geeignet)',
            },
          };

          if (this.ytbesprechung.alter) {
            schemaOrg.itemReviewed['audience'] = {
              '@type': 'Audience',
              'suggestedMinAge': this.ytbesprechung.alter
            }
          }

          let sameAs = [];
          if (this.ytbesprechung.channels) {
            this.ytbesprechung.channels.forEach(channel => {
              if (channel.channeluri) {
                sameAs.push(channel.channeluri);
              }
              if (channel.channelkidsuri) {
                sameAs.push(channel.channelkidsuri);
              }
            });
          }
          if (sameAs) {
            schemaOrg.itemReviewed['sameAs'] = sameAs;
          }

          // add ytbesprechung.hashtags to schemaOrg.itemReviewed
          if (this.ytbesprechung.hashtags) {
            schemaOrg.itemReviewed['keywords'] = this.ytbesprechung.hashtags;
          }

          let positiveNotes = '';
          if (this.ytbesprechung.positiv) {
            this.ytbesprechung.positiv.forEach(positiv => {
              positiveNotes += positiv.name + ':\n' + positiv.description + '\n\n';
            });
            schemaOrg['positiveNotes'] = positiveNotes;
          }

          let negativeNotes = '';
          if (this.ytbesprechung.negativ) {
            this.ytbesprechung.negativ.forEach(negativ => {
              negativeNotes += negativ.name + ':\n' + negativ.description + '\n\n';
            });
            schemaOrg['negativeNotes'] = negativeNotes;
          }

          script.text = JSON.stringify(schemaOrg);
        }
        if (shouldAppend) {
          document.head.appendChild(script);
        }
      }
    }
  }

  alterWort(alter: number): string {
    // 3 is Drei, 5 is Fünf, 7 is Sieben, 9 is Neun
    switch (alter) {
      case 3: return 'Drei';
      case 5: return 'Fünf';
      case 7: return 'Sieben';
      case 9: return 'Neun';
      default: return alter.toString();
    }
  }

  prettifyUrl(title: string): string {
    if (!title) title = '';
    let prettyString = title.toLocaleLowerCase();
    titleReplacements.forEach(val => {
      prettyString = prettyString.split(val.from).join(val.to);
    });
    return prettyString;
  }

  ngOnInit(): void {
  }

  onImgError(): void {
    this.picture = Constants.PLACEHOLDER_IMAGE_WITH_PATH
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  openLegend(): void {
    this.matDialog.open(LegendDialogComponent, {
      width: '400px',
      data: { mode: LegendDialogModes.ytbesprechung }
    });
  }
}
